import { useEffect, useState } from "react";
import styles from "./ScheduleList.module.css";
import {
  ENTRY_FEE,
  ENTRY_FEE_PGA,
  INITIAL_PURSE,
  INITIAL_PURSE_SEASON_3_FINAL,
  INITIAL_PURSE_ELEVATED,
  INITIAL_PURSE_PGA,
  PGA_TOURNAMENT_ID,
} from "../../utils/constants";
import Image from "next/image";
import Link from "next/link";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useWindowSize } from "../../hooks/useWindowSize";
import { motion } from "framer-motion";
import {
  isPossibleToJoin,
  isElevatedRound,
  isFreeAmateur,
  isPremiumAmateur,
  isPossibleToJoinAM,
} from "../../utils/tournaments";
import { Tournament } from "../../types/tournament";
import { useUI } from "../../context/UIContext";
import { utcTimeLocal } from "../../utils/time";
import dayjs from "dayjs";

enum TabsOptionsPro {
  Season_3 = "Season 3",
  Season_2 = "Season 2",
  Season_1 = "Season 1",
}

enum TabsOptionsAmateur {
  All = "All",
  Free = "Free",
  Premium = "Premium",
}

interface TournamentTab {
  value: string;
  label: TabsOptionsPro | TabsOptionsAmateur;
}

interface ScheduleListProps {
  firstSeasonTournaments?: Array<Tournament>;
  secondSeasonTournaments?: Array<Tournament>;
  thirdSeasonTournaments?: Array<Tournament>;
  amFreeTournaments?: Array<Tournament>;
  amPremiumTournaments?: Array<Tournament>;
  amAllTournaments?: Array<Tournament>;
  isAmateur?: boolean;
}

interface ConditionalLinkWrapperProps {
  condition: boolean;
  wrapper: (children: React.ReactNode) => JSX.Element;
  children: JSX.Element;
}

const ConditionalLinkWrapper = ({
  condition,
  wrapper,
  children,
}: ConditionalLinkWrapperProps) => (condition ? wrapper(children) : children);

const ScheduleList = ({
  firstSeasonTournaments,
  secondSeasonTournaments,
  thirdSeasonTournaments,
  amFreeTournaments,
  amPremiumTournaments,
  amAllTournaments,
  isAmateur,
}: ScheduleListProps) => {
  const { dispatch } = useUI();
  const { isDesktop } = useWindowSize();
  const [isFirstRender, setIsFirstRender] = useState(true);

  const tabsOptionsType = isAmateur ? TabsOptionsAmateur : TabsOptionsPro;
  const tabs = Object.keys(tabsOptionsType).map((key: string) => ({
    label: tabsOptionsType[key as keyof typeof tabsOptionsType],
    value: key,
  }));

  const [selectedTab, setSelectedTab] = useState<string>(tabs[0].value);
  const previousWinner = null; //TODO add this part when needed

  const tournamentsByTab = isAmateur
    ? {
        [TabsOptionsAmateur.All]: amAllTournaments,
        [TabsOptionsAmateur.Free]: amFreeTournaments,
        [TabsOptionsAmateur.Premium]: amPremiumTournaments,
      }
    : {
        [TabsOptionsPro.Season_3]: thirdSeasonTournaments,
        [TabsOptionsPro.Season_2]: secondSeasonTournaments,
        [TabsOptionsPro.Season_1]: firstSeasonTournaments,
      };

  const defaultTabBySelected = () => {
    const tabBySelected = tabs?.find((t) => t.value === selectedTab)?.label;

    if (!tabBySelected) {
      return isAmateur ? TabsOptionsAmateur.All : TabsOptionsPro.Season_2;
    }
    return tabBySelected;
  };

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
    }
  }, []);

  const isJoinAvailable = (tournament: Tournament) => {
    const isFinalSeason3 =
      tournament?.season === 3 &&
      tournament?.name?.toLowerCase()?.includes("final");

    if (!tournament.signUpStartTime || !tournament.signUpEndTime || isFinalSeason3) return false;
    const signupStart = dayjs(tournament.signUpStartTime);
    const signupEnd = dayjs(tournament.signUpEndTime);
    const currentDate = dayjs();
    return (
      tournament?.versionInfo?.state !== "DRAFT" &&
      currentDate.isAfter(signupStart) &&
      currentDate.isBefore(signupEnd) &&
      isPossibleToJoin(
        tournament?.participants?.allInvitationsCount -
          tournament?.participants?.declinedCount -
          tournament?.participants?.withdrawnCount
      )
    );
  };

  const viewVariants = {
    initial: {
      opacity: isDesktop ? 1 : 0.5,
      transform: isDesktop ? "translate(0, 0)" : "translate(0, 4rem)",
    },
    view: {
      opacity: 1,
      transform: "translate(0, 0)",
    },
  };

  const hoverVariants = {
    initial: {
      backgroundColor:
        "linear-gradient(90deg,rgba(97, 97, 97, 0.2) 0%,rgba(97, 97, 97, 0) 100.09%),#141414",
    },
    hover: {
      backgroundColor: "#212121",
    },
  };
  const hoverVariantsPga = {
    initial: {
      backgroundColor:
        "linear-gradient(90deg,rgba(0, 83, 255, 0.5) 0%,rgba(0, 83, 255, 0) 100.09%),#00205b",
    },
    hover: {
      backgroundColor: "#0053ff",
      opacity: 1,
    },
  };

  if (isFirstRender) return <div />;

  return (
    <div className={isAmateur ? styles.scheduleList : ""}>
      {!isAmateur && (
        <div className={styles.scheduleTabs}>
          {tabs.map((tab: TournamentTab) => (
            <div
              key={tab.value}
              onClick={() => setSelectedTab(tab.value)}
              className={`${styles.scheduleTab} ${
                tab.value === selectedTab ? styles.scheduleTabActive : ""
              }`}
            >
              {tab.label}
            </div>
          ))}
        </div>
      )}
      <motion.section
        className={styles.scheduleCards}
        viewport={{ once: true }}
        variants={viewVariants}
        whileInView="view"
        initial="initial"
        transition={{
          ease: "easeInOut",
          duration: 0.8,
        }}
      >
        {tournamentsByTab[defaultTabBySelected()]?.map(
          (tournament: Tournament) => {
            const signupUri =
              !!tournament?.locationsConfiguration?.length &&
              tournament?.locationsConfiguration[0].paymentUrl;

            const isElevated = isElevatedRound(
              tournament.season,
              tournament.name
            );

            const isFinalSeason2 =
              tournament.season === 2 &&
              tournament.name?.toLowerCase()?.includes("final");
            const isFinalSeason3 =
              tournament.season === 3 &&
              tournament.name?.toLowerCase()?.includes("final");

            const isPremiumAmateurRound = isPremiumAmateur(tournament);

            const isPGATournament = tournament.id === PGA_TOURNAMENT_ID;

            const getPurse = () => {
              if (isPGATournament) {
                return (
                  INITIAL_PURSE_PGA +
                  ENTRY_FEE_PGA *
                    (tournament?.participants?.allInvitationsCount -
                      tournament?.participants?.declinedCount -
                      tournament?.participants?.withdrawnCount)
                );
              }

              if (isFinalSeason3) return INITIAL_PURSE_SEASON_3_FINAL;
              if (isFinalSeason2) return INITIAL_PURSE;

              return (
                (isElevated ? INITIAL_PURSE_ELEVATED : INITIAL_PURSE) +
                ENTRY_FEE *
                  (tournament?.participants?.allInvitationsCount -
                    tournament?.participants?.declinedCount -
                    tournament?.participants?.withdrawnCount)
              );
            };

            const isPublishedTournament =
              tournament?.versionInfo?.state !== "DRAFT";

            return (
              <motion.div
                key={tournament.id}
                className={`${styles.scheduleCard}${
                  isPGATournament ? " " + styles.scheduleCardPga : ""
                }`}
                variants={isPGATournament ? hoverVariantsPga : hoverVariants}
                whileHover={isPublishedTournament ? "hover" : {}}
                transition={{
                  ease: "linear",
                  duration: 0.3,
                }}
                style={{
                  cursor: isPublishedTournament ? "pointer" : "default",
                }}
              >
                <ConditionalLinkWrapper
                  condition={isPublishedTournament}
                  wrapper={(children: React.ReactNode | React.ReactNode[]) => (
                    <Link
                      href={`${
                        isAmateur ? "/amateur" : ""
                      }/tournament/${tournament.name
                        ?.replace(/:/g, "")
                        .replace(/ - /g, " ")
                        .replace(/\//g, "-")
                        .replace(/#/g, "")
                        .split(" ")
                        .join("-")
                        .toLowerCase()}`}
                    >
                      <a
                        className={`${styles.scheduleCardLink}${
                          isPGATournament
                            ? " " + styles.scheduleCardLinkPga
                            : ""
                        }`}
                      >
                        {children}
                      </a>
                    </Link>
                  )}
                >
                  <div
                    className={`${styles.scheduleCardWrapper} ${
                      !isPublishedTournament ? styles.inactiveColor : ""
                    }`}
                  >
                    <div className={styles.scheduleCardHeadline}>
                      <div className={styles.scheduleCardDate}>
                        <span>
                          {utcTimeLocal(tournament.startDate, "MMM. D, H:mm z")}
                        </span>
                        {" - "}
                        <span>
                          {utcTimeLocal(tournament.endDate, "MMM. D, H:mm z")}
                        </span>
                      </div>
                      <div className={styles.scheduleCardTitle}>
                        {tournament.name}
                      </div>
                    </div>
                    <div className={styles.scheduleCardInfo}>
                      <div
                        className={`${styles.schedulePurse} ${
                          (!isAmateur && isElevated) ||
                          (isAmateur && isPremiumAmateurRound)
                            ? styles.brandColor
                            : ""
                        }`}
                      >
                        <div className={styles.schedulePurseTitle}>
                          {isAmateur ? "Entry fee" : "Purse"}
                        </div>
                        <div className={styles.schedulePurseAmount}>
                          {isAmateur
                            ? isFreeAmateur(tournament)
                              ? "Free"
                              : "$10"
                            : `$${getPurse().toLocaleString("en-US")}`}
                        </div>
                      </div>
                      {previousWinner && (
                        <div className={styles.scheduleWinner}>
                          <div className={styles.scheduleWinnerImg}>
                            <Image
                              src={""}
                              layout="fill"
                              objectFit="contain"
                              alt="winner image"
                            />
                          </div>
                          <div className={styles.scheduleWinnerInfo}>
                            <span className={styles.schedulePurseTitle}>
                              Previous winner
                            </span>
                            <span>WINNER</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </ConditionalLinkWrapper>
                {isJoinAvailable(tournament) &&
                  signupUri &&
                  !isAmateur &&
                  !isPGATournament && (
                    <div
                      className={`${styles.cardBoxText} ${styles.orange} ${styles.cta}`}
                      onClick={() => {
                        window.localStorage.setItem(
                          "next_signup_uri",
                          signupUri
                        );
                        dispatch({
                          type: "toggleJoinFlowInfoModal",
                          payload: {
                            isOpen: true,
                            title: tournament.name,
                            modalType: "signup",
                          },
                        });
                      }}
                    >
                      {isDesktop ? (
                        <span
                          className={`${styles.joinLink} ${styles.joinLinkText}`}
                        >
                          Join
                        </span>
                      ) : (
                        <ArrowForwardIosIcon className={styles.joinLink} />
                      )}
                    </div>
                  )}
                {isAmateur &&
                  isPossibleToJoinAM(
                    tournament.endDate,
                    isPublishedTournament
                  ) && (
                    <a
                      href={`https://portal.trackmangolf.com/player/tournaments/${tournament.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {isDesktop ? (
                        <span
                          className={`${styles.joinLink} ${styles.joinLinkText}`}
                        >
                          Join
                        </span>
                      ) : (
                        <ArrowForwardIosIcon className={styles.joinLink} />
                      )}
                    </a>
                  )}
              </motion.div>
            );
          }
        )}
      </motion.section>
    </div>
  );
};

export default ScheduleList;
